import { Input, InputType, InputOptionBox, InputErrors, Button } from '../../../../core/components/Index';
import { observer } from 'mobx-react';
import ApplyViewModel from './ApplyViewModel';
import { useParams } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import { Steps } from './ApplyModels';
import { Appl } from '../../../../core/Appl';

const FederalTaxWitholding = observer(() => {
  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');
  let { memberId } = useParams();
  useAsyncEffect(async () => {
    await vm.loadStepAsync(Steps.FederalTaxWithholding, memberId!);
  }, []);

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-32px tracking--1percent text-neutral-800 font-semibold mb-4">Tax Withholding</h1>
        <p className="text-black text-15px tracking--1percent leading-6">
          To ensure that the correct amount of tax is withheld from your retirement benefits, please set your federal and state tax withholding
          preferences below.{' '}
        </p>
      </div>
      <h4 className="text-gray-700 font-medium text-22px">Federal Tax Withholding</h4>
      <div className="space-y-8 text-black">
        <div className="flex items-center">
          <InputOptionBox
            inputId="federalTaxWithhold"
            label={<span><span className='font-bold'>Do not withhold</span> Federal income tax.</span>}
            value={vm.model.taxWithHolding.federalTaxWithHolding?.federalTaxWithhold!}
            checked={vm.model.taxWithHolding.federalTaxWithHolding?.federalTaxWithhold === "N"}
            onChange={(e: any) => vm.onFederalTaxWithholdStatusChange("N", e.target.checked)}
          />
        </div>

        <div className="flex items-center my-8 font-bold w-full">
          <div className="relative w-full">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[3px] border-[#E7EAEB]"></div>
            </div>
            <div className="relative pl-6">
              <span className="bg-white px-2 text-black text-15px">OR</span>
            </div>
          </div>
        </div>
        <div>
          <InputOptionBox
            inputId="federalTaxWithhold1"
            label={<span><span className='font-bold'>Withhold</span> Federal income tax based on your filing status (check one):</span>}
            value={vm.model.taxWithHolding.federalTaxWithHolding?.federalTaxWithhold}
            checked={vm.model.taxWithHolding.federalTaxWithHolding?.federalTaxWithhold === "Y"}
            onChange={(e: any) => vm.onFederalTaxWithholdStatusChange("Y", e.target.checked)}
          />
        </div>

        <div
          className={`${vm.model.taxWithHolding.federalTaxWithHolding?.federalTaxWithhold === 'Y' ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
          <div className={`space-y-2`}>
            {' '}
            <div className="pl-6 space-y-4 -mt-4 mb-8">
              {vm.model.withholdFederalIncomeTaxStatusListItems?.map((status, index) => (
                <div key={`federalWithholdIncomeTaxStatus-${index}`} className="flex items-center">
                  <InputOptionBox
                    inputId={`federalWithholdIncomeTaxStatus-${status.key}`}
                    label={status.value}
                    value={status.key}
                    checked={vm.model.taxWithHolding.federalTaxWithHolding?.federalWithholdIncomeTaxStatus === status.key.toString()}
                    onChange={(e: any) => vm.onInputChange(e, 'federalWithholdIncomeTaxStatus', vm.model.taxWithHolding.federalTaxWithHolding)}
                    disabled={vm.model.taxWithHolding.federalTaxWithHolding?.federalTaxWithhold !== "Y"}
                  />
                </div>
              ))}
              <InputErrors errors={Appl.Error?.items} viewId={'federalWithholdIncomeTaxStatus'} />
            </div>
            <h4 className="text-base font-bold mt-4">Claim Dependents (Optional)</h4>
            <p className="text-gray-900">Enter the amount of the child tax credit and the credit for other dependents.</p>
            <div className="h-full md:pl-6">
              <Input
                id="federalClaimsDependant"
                value={vm.model.taxWithHolding.federalTaxWithHolding?.federalClaimsDependant}
                inputType={InputType.Number}
                onChange={(e) => {
                  vm.onInputChange(e, 'federalClaimsDependant', vm.model.taxWithHolding.federalTaxWithHolding);
                }}
                prefix="$"
                prefixPosition="left"
                readOnly={vm.model.taxWithHolding.federalTaxWithHolding?.federalTaxWithhold !== "Y"}
              />
            </div>
          </div>
          <div className="mt-8 space-y-2">
            <h4 className="text-base font-bold mt-4">Other Adjustments (Optional)</h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:pl-6">
              <div>
                <p className="text-black text-15px tracking--1_5percent">a. Enter other income not from jobs, such as interest or dividends.</p>
                <div className="h-full">
                  <Input
                    id="federalIncomeNotFromJob"
                    value={vm.model.taxWithHolding.federalTaxWithHolding?.federalIncomeNotFromJob}
                    inputType={InputType.Number}
                    onChange={(e) => {
                      vm.onInputChange(e, 'federalIncomeNotFromJob', vm.model.taxWithHolding.federalTaxWithHolding);
                    }}
                    prefix="$"
                    prefixPosition="left"
                    readOnly={vm.model.taxWithHolding.federalTaxWithHolding?.federalTaxWithhold !== "Y"}
                  />
                </div>
              </div>
              <div>
                <p className="text-black text-15px tracking--1_5percent">b. Enter deductions claimed other than the standard deduction.</p>
                <div className="h-full">
                  <Input
                    id="federalDeductionsClaimed"
                    value={vm.model.taxWithHolding.federalTaxWithHolding?.federalDeductionsClaimed}
                    inputType={InputType.Number}
                    onChange={(e) => {
                      vm.onInputChange(e, 'federalDeductionsClaimed', vm.model.taxWithHolding.federalTaxWithHolding);
                    }}
                    prefix="$"
                    prefixPosition="left"
                    readOnly={vm.model.taxWithHolding.federalTaxWithHolding?.federalTaxWithhold !== "Y"}
                  />
                </div>
              </div>
              <div>
                <p className="text-black text-15px tracking--1_5percent">c. Enter any additional tax you want withheld from each pay period.</p>
                <div className="h-full">
                  <Input
                    id="federalAdditionalWithold"
                    value={vm.model.taxWithHolding.federalTaxWithHolding?.federalAdditionalWithold}
                    inputType={InputType.Number}
                    onChange={(e) => {
                      vm.onInputChange(e, 'federalAdditionalWithold', vm.model.taxWithHolding.federalTaxWithHolding);
                    }}
                    prefix="$"
                    prefixPosition="left"
                    readOnly={vm.model.taxWithHolding.federalTaxWithHolding?.federalTaxWithhold !== "Y"}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="h-2"></div>
      <div className="flex mt-6 flex-wrap gap-2 sm:flex-nowrap">
        <Button
          label="Save & Continue" rounded
          disabled={vm.model.taxWithHolding.federalTaxWithHolding?.federalTaxWithhold === ''}
          onClick={async () => await vm.onFederalTaxWithholdStepChangeAsync()}
          className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600"
        />
        {vm.navigatedFromReviewScreen && <Button title="Cancel & Return to Review" severity='secondary' rounded
          onClick={async () => await vm.navigateToReviewStepAsync()}
        />}
      </div>
    </div>

  );
});

export default FederalTaxWitholding;
